import React, { useState, useEffect } from 'react';
import type { IKpiCardProps } from '../types';

export const KpiCard = ({ kpi, delay, onClick }: IKpiCardProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (!kpi.slides || kpi.slides.length <= 1) return;

        const intervalId = setInterval(() => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % kpi.slides!.length);
        }, 5000); // Change slide every 5 seconds

        return () => clearInterval(intervalId);
    }, [kpi.slides]);

    // Render Banner Card if slides are present
    if (kpi.slides && kpi.slides.length > 0) {
        // This is the fix: Ensure the index is not out of bounds.
        // This can happen if the `slides` prop changes to a shorter array.
        const safeIndex = currentIndex >= kpi.slides.length ? 0 : currentIndex;
        const activeSlide = kpi.slides[safeIndex];

        // Add a guard in case slide data is malformed (e.g., array with undefined items)
        if (!activeSlide) return null;
        
        return (
            <button
            onClick={onClick}
            className="bg-surface rounded-lg stat-card fade-in border border-default text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1DB954] p-0 overflow-hidden relative h-full"
                style={{ animationDelay: delay }}
                aria-label={`Ver destaques da semana`}
            >
                {kpi.slides.map((slide, index) => (
                    <div
                        key={index}
                        className="carousel-slide"
                        style={{ backgroundImage: `url(${slide.imgSrc})`, opacity: index === safeIndex ? 1 : 0 }}
                        aria-hidden={index !== safeIndex}
                    />
                ))}

                <div className="carousel-overlay"></div>

                <div className="absolute top-0 left-0 right-0 p-3 sm:p-4 flex items-center justify-between">
                    <h3 className="font-semibold text-white backdrop-blur-sm bg-black/20 px-2 py-1 rounded-md">{kpi.title}</h3>
                    <i className={`${kpi.iconClass} text-2xl`}></i>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <h4 className="font-bold text-lg">{activeSlide.title}</h4>
                    <p className="text-sm text-zinc-300">{activeSlide.subtitle}</p>
                </div>

                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
                    {kpi.slides.map((_, index) => (
                        <div
                            key={index}
                            className={`carousel-dot ${index === safeIndex ? 'active' : ''}`}
                        />
                    ))}
                </div>
            </button>
        );
    }
    
    // Render original KPI Card
    return (
        <button 
            onClick={onClick}
            className="bg-surface p-4 rounded-lg stat-card fade-in border border-default flex flex-col text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1DB954] h-full"
            style={{ animationDelay: delay }}
            aria-label={`Ver detalhes de ${kpi.title}`}
        >
            <div className="flex-grow">
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-muted">{kpi.title}</h3>
                    <i className={`${kpi.iconClass} text-2xl`}></i>
                </div>
                <p className={`${kpi.subValue ? 'text-2xl' : 'text-3xl'} font-bold text-zinc-900 dark:text-white mt-1.5 truncate`}>{kpi.value}</p>
                {kpi.badgeMetrics && kpi.badgeMetrics.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {kpi.badgeMetrics.map((bm, i) => (
                            <span key={i} className="text-xs font-semibold px-2 py-0.5 rounded bg-subtle border border-default text-muted">
                                {bm.label}: <span className="text-primary ml-1">{bm.value}</span>
                            </span>
                        ))}
                    </div>
                )}
                {kpi.change ? (
                    <p className={`text-sm ${kpi.changeColor} flex items-center gap-1 mt-1`}><i className={kpi.changeIcon}></i><span>{kpi.change}</span></p>
                ) : (
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{kpi.subValue}</p>
                )}
            </div>
            {kpi.details && (
                <div className="mt-3 p-2.5 rounded-md bg-subtle border border-default">
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mb-0.5">{kpi.details.label}</p>
                    <p className="text-sm font-semibold text-zinc-900 dark:text-white flex items-center gap-2 truncate">
                        {kpi.details.iconClass && <i className={`${kpi.details.iconClass} text-zinc-500 dark:text-zinc-400`}></i>}
                        <span className="truncate">{kpi.details.value}</span>
                    </p>
                </div>
            )}
        </button>
    );
};
